/**
 * Centralized place for core logic to perform optimistic updates after editing a sequence's bases or parts.
 * Keep in sync with benchling/molbio/sequences/sequence_edits.py.
 *
 * Although some aspects of the frontend make for slightly different implementations here, the core logic
 * for doing updates should be kept as similar as possible to the backend.
 */
import {assert} from 'Asserts';
import {SequencePrimer} from 'BioJS';
import {Annotation} from 'biojs/Annotation';
import {Part} from 'biojs/Part';
import {Sequence} from 'biojs/Sequence';
import {Translation} from 'biojs/Translation';
import {ModifiedNucleotideAttributes} from 'constants/SequenceConstants';
import * as sequtil from 'molbio/SeqUtil';

export function replaceWithBases(sequence: Sequence, start: number, end: number, newBases: string): void {
  const oldSeqLen = sequence.length();
  const replaceStart = start;
  const replaceEnd = end === 0 ? oldSeqLen : end;

  if (replaceStart < replaceEnd) {
    replaceNonWrappingBases(sequence, replaceStart, replaceEnd, newBases, oldSeqLen);
  } else {
    assert(sequence.circular(), 'Expected circular sequence for replaceStart < replaceEnd');
    const replaceLen = oldSeqLen - replaceStart + replaceEnd;
    reindex(sequence, replaceStart);
    replaceNonWrappingBases(sequence, 0, replaceLen, newBases, oldSeqLen);
  }
}

export function deleteBases(sequence: Sequence, start: number, end: number): void {
  const oldSeqLen = sequence.length();
  const deleteStart = start;
  const deleteEnd = end === 0 ? oldSeqLen : end;

  if (deleteStart < deleteEnd) {
    replaceNonWrappingBases(sequence, deleteStart, deleteEnd, '', oldSeqLen);
  } else {
    assert(sequence.circular(), 'Expected circular sequence for replaceStart < replaceEnd');
    // When deleting across the origin, first rotate the sequence so the deleted range is entirely at the
    // start. This way the deletion can happen as a single operation.
    const deleteLen = oldSeqLen - deleteStart + deleteEnd;
    reindex(sequence, deleteStart);
    replaceNonWrappingBases(sequence, 0, deleteLen, '', oldSeqLen);
  }
}

export function reindex(sequence: Sequence, pos: number): void {
  assert(
    0 <= pos && pos <= sequence.length(),
    `Reindex position out of sequence bounds: pos=${pos}, sequence.length()=${sequence.length()}`
  );
  sequence.set('bases', sequence.bases().slice(pos) + sequence.bases().slice(0, pos));

  for (const feature of getRangeFeatures(sequence)) {
    const newStart = sequtil.getReindexedPosition(
      (feature as Annotation).get('start'),
      pos,
      sequence.length()
    );
    const newEnd = sequtil.getReindexedPosition((feature as Annotation).get('end'), pos, sequence.length());
    (feature as Annotation).set({start: newStart, end: newEnd});
  }

  for (const primer of sequence.sequencePrimers().models) {
    const newBindPosition = sequtil.getReindexedPosition(
      primer.bindPosition(sequence),
      pos,
      sequence.length()
    );
    primer.setBindPosition(sequence, newBindPosition, primer.overhangLength());
  }
}

export function insertBases(sequence: Sequence, insertPos: number, bases: string): void {
  const oldSeqLen = sequence.length();
  replaceNonWrappingBases(sequence, insertPos, insertPos, bases, oldSeqLen);
}

function replaceNonWrappingBases(
  sequence: Sequence,
  replaceStart: number,
  replaceEnd: number,
  newBases: string,
  oldSeqLen: number
): void {
  assert(
    0 <= replaceStart && replaceStart <= replaceEnd && replaceEnd <= oldSeqLen,
    `Expected 0 <= replaceStart <= replaceEnd <= sequence length, got replaceStart=${replaceStart}, replaceEnd=${replaceEnd}, length=${oldSeqLen}`
  );

  sequence.set(
    'bases',
    sequence.bases().slice(0, replaceStart) + newBases + sequence.bases().slice(replaceEnd)
  );
  const toDelete: Array<Annotation | Translation | SequencePrimer | Part> = [
    ...getRangeFeatures(sequence).filter((f) =>
      shouldDeleteRangeFeature(f, replaceStart, replaceEnd, newBases.length, oldSeqLen)
    ),
    ...sequence
      .sequencePrimers()
      .models.filter((p) => shouldDeletePrimer(p, replaceStart, replaceEnd, oldSeqLen)),
  ];

  for (const feature of toDelete) {
    feature.trigger('destroy', feature, feature.collection);
  }

  for (const feature of getRangeFeatures(sequence)) {
    applyReplaceToRangeFeature(feature, replaceStart, replaceEnd, newBases.length, oldSeqLen);
  }

  for (const primer of sequence.sequencePrimers().models) {
    applyReplaceToPrimer(sequence, primer, replaceStart, replaceEnd, newBases.length, oldSeqLen);
  }

  sequence.set(
    'modifiedNucleotides',
    sequence
      .modifiedNucleotides()
      .map((mod) => applyReplaceToModifiedNucleotide(mod, replaceStart, replaceEnd, newBases.length))
      .filter((maybeMod) => Boolean(maybeMod)) as Array<Readonly<ModifiedNucleotideAttributes>>
  );
}

function getRangeFeatures(sequence: Sequence): Array<Annotation | Translation | Part> {
  return [...sequence.annotations().models, ...sequence.translations().models, ...sequence.parts().models];
}

function applyReplaceToRangeFeature(
  feature: Annotation | Translation | Part,
  replaceStart: number,
  replaceEnd: number,
  newBasesLen: number,
  oldSeqLen: number
): void {
  let featureStart = (feature as Annotation).get('start');
  let featureEnd = (feature as Annotation).get('end') === 0 ? oldSeqLen : (feature as Annotation).get('end');

  if (replaceStart < featureStart && featureStart < replaceEnd) {
    featureStart = replaceEnd;
  }

  if (replaceStart < featureEnd && featureEnd < replaceEnd) {
    featureEnd = replaceStart;
  }

  const replaceLen = newBasesLen - (replaceEnd - replaceStart);
  if (featureStart >= replaceEnd) {
    featureStart += replaceLen;
  }
  if (featureEnd > replaceStart) {
    featureEnd += replaceLen;
  }

  featureStart = sequtil.mod(featureStart, oldSeqLen + replaceLen);
  featureEnd = sequtil.mod(featureEnd, oldSeqLen + replaceLen);

  (feature as Annotation).set({start: featureStart, end: featureEnd});
}

function shouldDeleteRangeFeature(
  feature: Annotation | Translation | Part,
  replaceStart: number,
  replaceEnd: number,
  newBasesLen: number,
  oldSeqLen: number
): boolean {
  const featureStart = (feature as Annotation).get('start');
  const featureEnd =
    (feature as Annotation).get('end') === 0 ? oldSeqLen : (feature as Annotation).get('end');

  if (
    feature instanceof Translation &&
    featureStart === replaceStart &&
    featureEnd === replaceEnd &&
    newBasesLen > 0
  ) {
    return false;
  }

  if (featureStart >= featureEnd) {
    return replaceStart === 0 && replaceEnd === oldSeqLen;
  } else {
    return replaceStart <= featureStart && featureEnd <= replaceEnd;
  }
}

function applyReplaceToPrimer(
  sequence: Sequence,
  primer: SequencePrimer,
  replaceStart: number,
  replaceEnd: number,
  newBasesLen: number,
  oldSeqLen: number
): void {
  let bindPosition = primer.bindPosition({
    length(): number {
      return oldSeqLen;
    },
  });

  const replaceLen = newBasesLen - (replaceEnd - replaceStart);
  if (bindPosition >= replaceEnd) {
    bindPosition += replaceLen;
  }
  const overhangLength = primer.overhangLength();

  primer.setBindPosition(sequence, bindPosition, overhangLength);
}

function shouldDeletePrimer(
  primer: SequencePrimer,
  replaceStart: number,
  replaceEnd: number,
  oldSeqLen: number
): boolean {
  const bindPosition = primer.bindPosition({
    length(): number {
      return oldSeqLen;
    },
  });
  return replaceStart <= bindPosition && bindPosition < replaceEnd;
}

function applyReplaceToModifiedNucleotide(
  modifiedNucleotide: ModifiedNucleotideAttributes,
  replaceStart: number,
  replaceEnd: number,
  newBasesLen: number
): ModifiedNucleotideAttributes | undefined {
  let position = modifiedNucleotide.position;

  if (replaceStart <= position && position < replaceEnd) {
    return;
  }

  const replaceLen = newBasesLen - (replaceEnd - replaceStart);
  if (position >= replaceEnd) {
    position += replaceLen;
  }

  return {
    ...modifiedNucleotide,
    position,
  };
}
